import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiUsers,
  FiClipboard,
  FiTrendingUp,
  FiSettings,
  FiHelpCircle,
  FiBox,
  FiGrid,
  FiBarChart2,
  FiLayers,
  FiGift,
  FiUserPlus,
  FiCheckSquare,
  FiMessageSquare,
  FiLifeBuoy,
  FiRadio,
  FiBriefcase,
  FiFileText,
  FiUserCheck,
  FiDollarSign,
  FiZap,
  FiTarget,
  FiAward,
  FiUploadCloud,
  FiImage,
  FiAtSign,
  FiBell,
  FiMail,
  FiBookOpen,
  FiShield,
  FiShoppingBag,
  FiCreditCard,
  FiBook,
  FiTicket,
  FiSend
} = FiIcons;

const NavLink = ({ item }) => {
  const location = useLocation();

  return (
    <Link
      to={item.path}
      className={`flex items-center p-3 my-1 rounded-lg transition-all duration-200 ease-in-out ${
        location.pathname === item.path
          ? 'bg-brand-secondary text-white shadow-lg'
          : 'text-gray-400 hover:bg-gray-700 hover:text-white'
      }`}
    >
      <SafeIcon icon={item.icon} className="h-5 w-5 mr-4" />
      <span className="font-medium">{item.name}</span>
    </Link>
  );
};

const SideNavigation = ({ currentPortal, isOpen }) => {
  const { user } = useContext(AuthContext);

  const getInternalPortalMenu = (user) => {
    const menu = [
      {
        title: 'Navigation',
        items: [
          { name: 'Dashboard', path: '/portal-dashboard', icon: FiGrid },
          { name: 'Dealrooms', path: '/dealrooms', icon: FiBriefcase },
          { name: 'Analytics', path: '/analytics', icon: FiBarChart2 },
          { name: 'Patient Journey', path: '/patient-journey', icon: FiZap },
        ],
      },
      {
        title: 'Communications',
        items: [
          { name: 'Chat', path: '/chat', icon: FiMessageSquare },
          { name: 'Communications Hub', path: '/communications', icon: FiMail },
          { name: 'AI Chat', path: '/ai-chat', icon: FiSend },
        ],
      },
      {
        title: 'Management',
        items: [
          { name: 'Referrals', path: '/referrals', icon: FiUsers },
          { name: 'Providers', path: '/provider-directory', icon: FiUserPlus },
          { name: 'Claims', path: '/claims', icon: FiCheckSquare },
          { name: 'Team', path: '/team', icon: FiUserCheck },
        ],
      },
      {
        title: 'Engagement',
        items: [
          { name: 'Rewards Center', path: '/rewards', icon: FiGift },
          { name: 'Challenges', path: '/challenges', icon: FiTarget },
          { name: 'Team Challenges', path: '/team-challenges', icon: FiAward },
          { name: 'Promotions', path: '/promotions', icon: FiDollarSign },
          { name: 'Product Recommendations', path: '/product-recommendations', icon: FiShoppingBag },
        ],
      },
      {
        title: 'Marketing & Content',
        items: [
          { name: 'Marketing', path: '/marketing', icon: FiBell },
          { name: 'AI Hub', path: '/ai-hub', icon: FiBook },
        ],
      },
      {
        title: 'Billing & Settings',
        items: [
          { name: 'Pricing', path: '/pricing', icon: FiCreditCard },
          { name: 'Subscriptions', path: '/subscriptions', icon: FiDollarSign },
          { name: 'Automation Center', path: '/automation-center', icon: FiZap },
          { name: 'Portal Customization', path: '/portal-customization', icon: FiLayers },
          { name: 'Import CSV', path: '/import-csv', icon: FiUploadCloud },
          { name: 'Profile', path: '/settings', icon: FiSettings },
          { name: 'Help Center', path: '/help', icon: FiHelpCircle },
        ],
      },
    ];

    // Add SuperAdmin-only items
    if (user?.role === 'SuperAdmin') {
      // Add to Management section
      const managementSection = menu.find((s) => s.title === 'Management');
      if (managementSection) {
        managementSection.items.push({
          name: 'User Roles',
          path: '/user-roles',
          icon: FiShield,
        });
      }

      // Add STL Blues Tickets to Engagement section (SuperAdmin only)
      const engagementSection = menu.find((s) => s.title === 'Engagement');
      if (engagementSection) {
        engagementSection.items.push({
          name: 'STL Blues Tickets',
          path: '/tickets',
          icon: FiTicket,
        });
      }
    }

    return menu;
  };

  const internalPortalMenu = getInternalPortalMenu(user);

  const communityPortalMenu = [
    { name: 'Community Feed', path: '/community', icon: FiUsers },
    { name: 'Learning Center', path: '/lms', icon: FiBookOpen },
    { name: 'Blog', path: '/blog', icon: FiMessageSquare },
    { name: 'Discussions', path: '/community/discussions', icon: FiMessageSquare },
    { name: 'Help Center', path: '/help', icon: FiHelpCircle },
  ];

  const menuItems = currentPortal === 'internal' ? internalPortalMenu : communityPortalMenu;

  return (
    <nav
      className={`bg-gray-800 text-white flex flex-col transition-all duration-300 ease-in-out ${
        isOpen ? 'w-64' : 'w-0 overflow-hidden'
      }`}
    >
      <div className="flex-1 p-4 space-y-6 overflow-y-auto">
        {currentPortal === 'internal' ? (
          internalPortalMenu.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-3">
                {section.title}
              </h3>
              {section.items.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
            </div>
          ))
        ) : (
          communityPortalMenu.map((item) => <NavLink key={item.name} item={item} />)
        )}
      </div>

      <div className="p-4 border-t border-gray-700">
        <NavLink item={{ name: 'Help & Support', path: '/help', icon: FiHelpCircle }} />
      </div>
    </nav>
  );
};

export default SideNavigation;